import {
  DEFAULT_CSPROJ_FILE,
  DEFAULT_CS_ENTRY_FILE,
  defaultCsprojXml,
} from "./csproj";
import { DEFAULT_ICON_FILE, DEFAULT_ICON_SVG } from "./icon";
import { DEFAULT_INDICATOR_CODE } from "./default-indicator-code";
import type {
  CustomIndicatorProjectKind,
  EditorLanguage,
  ProjectFileNode,
  ProjectTreeNode,
  ProjectTreeNodeKind,
} from "./types";

export const DEFAULT_ENTRY_FILE = DEFAULT_CS_ENTRY_FILE;
export const DEFAULT_CSPROJ_PATH = DEFAULT_CSPROJ_FILE;
export const DEFAULT_README_FILE = "README.md";

export interface DefaultProjectFile {
  path: string;
  content: string;
  type: "file" | "folder";
}

export function getDefaultProjectFiles(
  projectName: string,
  kind: CustomIndicatorProjectKind = "indicator",
  options?: {
    templateCode?: string;
    version?: string;
    description?: string;
    overlay?: boolean;
  }
): DefaultProjectFile[] {
  const name = projectName.trim() || "My Custom Indicator";
  const entryCode = options?.templateCode ?? DEFAULT_INDICATOR_CODE;
  return [
    {
      path: DEFAULT_CSPROJ_PATH,
      content: defaultCsprojXml({
        name,
        kind,
        version: options?.version,
        description: options?.description,
        overlay: options?.overlay,
      }),
      type: "file",
    },
    {
      path: DEFAULT_ENTRY_FILE,
      content: entryCode,
      type: "file",
    },
    {
      path: DEFAULT_README_FILE,
      content: `# ${name}\n\nCustom C# indicator project (Vybe Trader).\n`,
      type: "file",
    },
    { path: "src", content: "", type: "folder" },
    { path: "src/functions", content: "", type: "folder" },
    { path: "docs", content: "", type: "folder" },
    { path: "assets", content: "", type: "folder" },
    {
      path: DEFAULT_ICON_FILE,
      content: DEFAULT_ICON_SVG,
      type: "file",
    },
  ];
}

/** Normalize path: forward slashes, no leading/trailing slashes, no duplicate segments */
export function normalizePath(path: string): string {
  return path
    .replace(/\\/g, "/")
    .split("/")
    .filter((seg) => seg.length > 0 && seg !== ".")
    .join("/");
}

export function joinPath(...parts: string[]): string {
  return normalizePath(parts.join("/"));
}

export function dirname(path: string): string {
  const normalized = normalizePath(path);
  const idx = normalized.lastIndexOf("/");
  if (idx <= 0) return "";
  return normalized.slice(0, idx);
}

export function basename(path: string): string {
  const normalized = normalizePath(path);
  const idx = normalized.lastIndexOf("/");
  return idx >= 0 ? normalized.slice(idx + 1) : normalized;
}

/** Basenames of direct children under parentPath ("" = project root). */
export function listChildNames(
  files: Pick<ProjectFileNode, "path">[],
  parentPath: string
): string[] {
  const parent = normalizePath(parentPath);
  return files
    .filter((f) => dirname(f.path) === parent)
    .map((f) => basename(f.path));
}

/** Return base or base-1, base-2, … when name already exists among siblings. */
export function uniqueChildName(existingNames: string[], base: string): string {
  const normalizedBase = base.trim() || "untitled";
  if (!existingNames.includes(normalizedBase)) return normalizedBase;

  const dot = normalizedBase.lastIndexOf(".");
  const hasExt = dot > 0;
  const stem = hasExt ? normalizedBase.slice(0, dot) : normalizedBase;
  const ext = hasExt ? normalizedBase.slice(dot) : "";

  let index = 1;
  let candidate = `${stem}-${index}${ext}`;
  while (existingNames.includes(candidate)) {
    index += 1;
    candidate = `${stem}-${index}${ext}`;
  }
  return candidate;
}

export function fileId(projectId: string, path: string): string {
  return `${projectId}:${normalizePath(path)}`;
}

export function parseFileId(id: string): { projectId: string; path: string } {
  const idx = id.indexOf(":");
  if (idx <= 0) {
    return { projectId: id, path: "" };
  }
  return {
    projectId: id.slice(0, idx),
    path: id.slice(idx + 1),
  };
}

const INVALID_PATH_CHARS = /[<>:"|?*\x00-\x1f]/;
const RESERVED_NAMES = new Set([".", ".."]);

export function validatePath(path: string): { ok: boolean; error?: string } {
  const normalized = normalizePath(path);
  if (!normalized) {
    return { ok: false, error: "Path cannot be empty" };
  }
  const segments = normalized.split("/");
  for (const seg of segments) {
    if (RESERVED_NAMES.has(seg)) {
      return { ok: false, error: `Invalid path segment: ${seg}` };
    }
    if (INVALID_PATH_CHARS.test(seg)) {
      return { ok: false, error: `Invalid characters in path: ${seg}` };
    }
  }
  return { ok: true };
}

export function renamePath(oldPath: string, newName: string): string {
  const dir = dirname(oldPath);
  const newPath = dir ? joinPath(dir, newName) : normalizePath(newName);
  return newPath;
}

export function movePath(oldPath: string, targetFolderPath: string): string {
  const name = basename(oldPath);
  if (!targetFolderPath) return name;
  return joinPath(targetFolderPath, name);
}

export function extToLanguage(path: string): EditorLanguage {
  const ext = path.split(".").pop()?.toLowerCase() ?? "";
  switch (ext) {
    case "cs":
    case "viis":
      return "csharp";
    case "csproj":
      return "xml";
    case "md":
    case "markdown":
      return "markdown";
    case "json":
      return "json";
    case "svg":
      return "svg";
    case "png":
    case "webp":
    case "jpg":
    case "jpeg":
    case "gif":
      return "image";
    default:
      return "text";
  }
}

export function extToNodeKind(path: string): ProjectTreeNodeKind {
  const ext = path.split(".").pop()?.toLowerCase() ?? "";
  switch (ext) {
    case "cs":
      return "cs";
    case "csproj":
      return "csproj";
    case "viis":
      return "viis";
    case "md":
    case "markdown":
      return "md";
    case "json":
      return "json";
    case "canvas":
      return "canvas";
    case "svg":
      return "svg";
    case "png":
    case "webp":
    case "jpg":
    case "jpeg":
    case "gif":
      return "image";
    default:
      return "text";
  }
}

export function buildTree(
  files: ProjectFileNode[],
  rootLabel: string
): ProjectTreeNode {
  const sorted = [...files].sort((a, b) => {
    if (a.type !== b.type) {
      return a.type === "folder" ? -1 : 1;
    }
    return a.path.localeCompare(b.path);
  });

  const nodeMap = new Map<string, ProjectTreeNode>();

  for (const file of sorted) {
    const kind =
      file.type === "folder" ? "folder" : extToNodeKind(file.path);
    nodeMap.set(file.path, {
      path: file.path,
      label: basename(file.path),
      kind,
      type: file.type,
      children: file.type === "folder" ? [] : undefined,
    });
  }

  const root: ProjectTreeNode = {
    path: "",
    label: rootLabel.trim() || "My Custom Indicator",
    kind: "folder",
    type: "folder",
    children: [],
  };

  for (const file of sorted) {
    const node = nodeMap.get(file.path);
    if (!node) continue;

    const parentPath = dirname(file.path);
    if (!parentPath) {
      root.children!.push(node);
    } else {
      let parent = nodeMap.get(parentPath);
      if (!parent) {
        parent = {
          path: parentPath,
          label: basename(parentPath),
          kind: "folder",
          type: "folder",
          children: [],
        };
        nodeMap.set(parentPath, parent);
        const grandParent = dirname(parentPath);
        if (!grandParent) {
          root.children!.push(parent);
        } else {
          const gp = nodeMap.get(grandParent);
          if (gp?.children) gp.children.push(parent);
          else root.children!.push(parent);
        }
      }
      parent.children!.push(node);
    }
  }

  sortTreeChildren(root);
  return root;
}

function sortTreeChildren(node: ProjectTreeNode): void {
  if (!node.children) return;
  node.children.sort((a, b) => {
    if (a.type !== b.type) {
      return a.type === "folder" ? -1 : 1;
    }
    return a.label.localeCompare(b.label);
  });
  for (const child of node.children) {
    sortTreeChildren(child);
  }
}

export function collectDescendantPaths(
  path: string,
  files: ProjectFileNode[]
): string[] {
  const prefix = path ? `${path}/` : "";
  return files
    .filter((f) => f.path === path || f.path.startsWith(prefix))
    .map((f) => f.path);
}

export function computeProjectSizeBytes(files: ProjectFileNode[]): number {
  return files
    .filter((f) => f.type === "file")
    .reduce((sum, f) => sum + f.sizeBytes, 0);
}

export function getEntryContent(
  files: ProjectFileNode[],
  entryFilePath: string
): string {
  const entry = files.find(
    (f) => f.type === "file" && f.path === entryFilePath
  );
  return entry?.content ?? "";
}
